import { closestCenter, DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Checkbox,
  Label,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Spinner,
} from '@ui/components';
import { DownloadIcon, FileTextIcon, SheetIcon } from 'lucide-react';
import * as React from 'react';
import { AppTableRef } from '../app-table/types/table-props';
import {
  ExportColumn,
  useAppTableExportSheet,
} from './lib/hooks/use-app-table-export-sheet';

interface AppTableExportSheetProps<TData> {
  tableRef: React.RefObject<AppTableRef<TData>>;
  onExcel?: (columns: string[], rows: Partial<TData>[]) => void;
  onPdf?: (columns: string[], rows: Partial<TData>[]) => void;
  title?: string;
  triggerLabel?: string;
  filters?: React.ReactNode;
  disabled?: boolean;
}

interface ExportColumnRowProps {
  column: ExportColumn;
  isSelected: boolean;
  onToggle: (id: string, value: boolean) => void;
}

const ExportColumnRow = React.memo<ExportColumnRowProps>(
  ({ column, isSelected, onToggle }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: column.id });

    return (
      <div
        ref={setNodeRef}
        className="flex cursor-grab items-center gap-1 rounded-md border border-transparent p-2 hover:bg-muted/30"
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}
        {...attributes}
        {...listeners}
      >
        <Checkbox
          checked={isSelected}
          id={`export-column-${column.id}`}
          onCheckedChange={(value) => onToggle(column.id, value === true)}
        />
        <Label
          className="text-sm"
          htmlFor={`export-column-${column.id}`}
        >
          {column.label}
        </Label>
      </div>
    );
  },
);

ExportColumnRow.displayName = 'ExportColumnRow';

export const AppTableExportSheet = <TData,>({
  tableRef,
  onExcel,
  onPdf,
  title = 'Export table',
  triggerLabel = 'Export',
  filters,
  disabled,
}: AppTableExportSheetProps<TData>) => {
  const {
    hasExportHandlers,
    isOpen,
    handleOpenChange,
    columns,
    selectedColumnIds,
    selectedColumnSet,
    sensors,
    handleToggleColumn,
    handleDragEnd,
    isSubmitting,
    exportExcel,
    exportPdf,
  } = useAppTableExportSheet({ tableRef, onExcel, onPdf });

  if (!hasExportHandlers) return null;

  return (
    <Sheet
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <SheetTrigger asChild>
        <Button
          className="h-10"
          disabled={disabled}
          variant="outline"
        >
          <DownloadIcon className="mr-2 size-4" />
          {triggerLabel}
        </Button>
      </SheetTrigger>
      <SheetContent className="flex flex-col gap-4 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        {!!filters && (
          <div className="rounded-md border p-4">
            <div className="text-sm font-semibold">Filters</div>
            <div className="mt-3 flex flex-wrap gap-4">{filters}</div>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-auto rounded-md border p-3">
          {columns.length ? (
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              sensors={sensors}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={columns.map((column) => column.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid gap-1">
                  {columns.map((column) => (
                    <ExportColumnRow
                      key={column.id}
                      column={column}
                      isSelected={selectedColumnSet.has(column.id)}
                      onToggle={handleToggleColumn}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="py-6 text-center text-sm text-gray-500">
              No columns found.
            </div>
          )}
        </div>
        <SheetFooter>
          {!!onPdf && (
            <Button
              aria-busy={isSubmitting}
              disabled={!selectedColumnIds.length || isSubmitting}
              type="button"
              variant="outline"
              onClick={exportPdf}
            >
              {isSubmitting ? (
                <Spinner className="mr-2 size-4" />
              ) : (
                <FileTextIcon className="mr-2 size-4" />
              )}
              PDF
            </Button>
          )}
          {!!onExcel && (
            <Button
              aria-busy={isSubmitting}
              disabled={!selectedColumnIds.length || isSubmitting}
              type="button"
              onClick={exportExcel}
            >
              {isSubmitting ? (
                <Spinner className="mr-2 size-4" />
              ) : (
                <SheetIcon className="mr-2 size-4" />
              )}
              Excel
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
